import { useState } from 'react';
import {
    ChevronDown, Pencil, Trash2, RefreshCw, ArrowUp, ArrowDown,
    Clock, Code2, MessageSquare, Lightbulb, Check, X, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TagInput } from '@/components/ui/TagInput';
import { cn } from '@/lib/utils';

// ── Constants ──────────────────────────────────────────────────────────────────
const TYPE_COLORS = {
    technical:   'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/25',
    behavioral:  'bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/25',
    situational: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    cultural:    'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    coding:      'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/25',
};
const DIFF_COLORS = {
    easy:   'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25',
    medium: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/25',
    hard:   'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/25',
};
const QUESTION_TYPES = ['technical', 'behavioral', 'situational', 'cultural', 'coding'];
const DIFFICULTIES   = ['easy', 'medium', 'hard'];
const OPTION_LABELS  = ['A', 'B', 'C', 'D'];

function secsToLabel(secs) {
    const s = Number(secs) || 0;
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r === 0 ? `${m} min` : `${m}m ${r}s`;
}

function blankOptions() {
    return OPTION_LABELS.map(l => ({ label: l, text: '', isCorrect: false }));
}

function optionsFromQuestion(q) {
    if (!q.options || q.options.length === 0) return blankOptions();
    // Fill up to 4 options, preserving existing
    const filled = OPTION_LABELS.map((l, i) => ({
        label: l,
        text:  q.options[i]?.text  || '',
        isCorrect: q.options[i]?.isCorrect || false,
    }));
    // If none is marked correct, default to first
    const hasCorrect = filled.some(o => o.isCorrect);
    if (!hasCorrect && filled[0]) filled[0].isCorrect = true;
    return filled;
}

/**
 * QuestionCard — one question row in the QuestionSetEditorModal.
 */
export function QuestionCard({
    question,
    index,
    total,
    onEdit,
    onDelete,
    onMoveUp,
    onMoveDown,
    onRegen,
    regenLoading = false,
    disabled = false,
}) {
    const [expanded, setExpanded] = useState(false);

    const [editText,     setEditText]     = useState('');
    const [editIdeal,    setEditIdeal]    = useState('');
    const [editKeywords, setEditKeywords] = useState([]);
    const [editTimeSecs, setEditTimeSecs] = useState(300);
    const [editType,     setEditType]     = useState('technical');
    const [editDiff,     setEditDiff]     = useState('medium');
    const [editFormat,   setEditFormat]   = useState('verbal'); // 'verbal' | 'mcq'
    const [editOptions,  setEditOptions]  = useState(blankOptions());

    const openEdit = () => {
        setEditText(question.questionText     || '');
        setEditIdeal(question.idealAnswer     || '');
        setEditKeywords(question.keywords     || []);
        setEditTimeSecs(question.timeLimitSeconds || 300);
        setEditType(question.questionType     || 'technical');
        setEditDiff(question.difficulty       || 'medium');
        setEditFormat(question.questionFormat || 'verbal');
        setEditOptions(optionsFromQuestion(question));
        setExpanded(true);
    };

    const cancelEdit = () => setExpanded(false);

    const applyEdit = () => {
        const update = {
            questionText:     editText.trim(),
            idealAnswer:      editIdeal.trim(),
            keywords:         editKeywords,
            timeLimitSeconds: Math.max(30, Number(editTimeSecs)),
            questionType:     editType,
            difficulty:       editDiff,
            questionFormat:   editFormat,
        };
        if (editFormat === 'mcq') {
            update.options = editOptions
                .filter(o => o.text.trim())
                .map(o => ({ text: o.text.trim(), isCorrect: o.isCorrect }));
        } else {
            update.options = [];
        }
        onEdit(update);
        setExpanded(false);
    };

    const setOptionText = (idx, text) =>
        setEditOptions(prev => prev.map((o, i) => i === idx ? { ...o, text } : o));

    const setCorrect = (idx) =>
        setEditOptions(prev => prev.map((o, i) => ({ ...o, isCorrect: i === idx })));

    const isMcq = question.questionFormat === 'mcq';

    return (
        <div className={cn(
            'rounded-xl border border-border bg-card transition-all',
            disabled && 'opacity-60 pointer-events-none',
        )}>
            {/* ── Collapsed row ─────────────────────────────────────────── */}
            <div className="flex items-start gap-3 p-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-bold text-primary mt-0.5">
                    {index + 1}
                </span>

                <div className="flex-1 min-w-0 space-y-1.5">
                    <p className="text-sm font-medium leading-snug line-clamp-2 text-foreground">
                        {question.questionText}
                    </p>
                    <div className="flex flex-wrap gap-1 items-center">
                        {isMcq && (
                            <span className="inline-flex items-center gap-0.5 rounded-md border border-indigo-500/25 bg-indigo-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700 dark:text-indigo-400">
                                <ListChecks className="h-2.5 w-2.5" />
                                MCQ
                            </span>
                        )}
                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', TYPE_COLORS[question.questionType] || TYPE_COLORS.technical)}>
                            {question.questionType === 'coding' && <Code2 className="mr-0.5 h-2.5 w-2.5" />}
                            {question.questionType}
                        </span>
                        <span className={cn('inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold', DIFF_COLORS[question.difficulty] || DIFF_COLORS.medium)}>
                            {question.difficulty}
                        </span>
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                            <Clock className="h-2.5 w-2.5" />
                            {secsToLabel(question.timeLimitSeconds)}
                        </span>
                        {!isMcq && question.keywords?.length > 0 && (
                            <span className="text-[10px] text-muted-foreground">
                                {question.keywords.slice(0, 3).join(', ')}{question.keywords.length > 3 ? ` +${question.keywords.length - 3}` : ''}
                            </span>
                        )}
                        {isMcq && question.options?.length > 0 && (
                            <span className="text-[10px] text-muted-foreground">
                                {question.options.length} options · correct: {question.options.find(o => o.isCorrect)?.text?.slice(0, 20) || '—'}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-0.5 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveUp}   disabled={index === 0}        title="Move up">
                        <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onMoveDown} disabled={index === total - 1} title="Move down">
                        <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={expanded ? cancelEdit : openEdit} title={expanded ? 'Close' : 'Edit'}>
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onRegen} disabled={regenLoading} title="Regenerate with AI">
                        <RefreshCw className={cn('h-3.5 w-3.5', regenLoading && 'animate-spin')} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-destructive" onClick={onDelete} title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => expanded ? cancelEdit() : openEdit()} title="Expand">
                        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')} />
                    </Button>
                </div>
            </div>

            {/* ── Expanded edit form ─────────────────────────────────────── */}
            {expanded && (
                <div className="border-t border-border bg-muted/20 rounded-b-xl p-4 space-y-4">

                    {/* Format toggle: Verbal / MCQ */}
                    <div className="space-y-1.5">
                        <Label className="text-xs">Answer format</Label>
                        <div className="flex gap-2">
                            {['verbal', 'mcq'].map(fmt => (
                                <button
                                    key={fmt}
                                    type="button"
                                    onClick={() => setEditFormat(fmt)}
                                    className={cn(
                                        'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                                        editFormat === fmt
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-border bg-background text-muted-foreground hover:border-primary/50',
                                    )}
                                >
                                    {fmt === 'verbal'
                                        ? <><MessageSquare className="h-3 w-3" /> Verbal</>
                                        : <><ListChecks   className="h-3 w-3" /> MCQ</>
                                    }
                                </button>
                            ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            {editFormat === 'mcq'
                                ? 'Candidate selects an option and records their reasoning aloud.'
                                : 'Candidate answers verbally. AI evaluates against ideal answer.'}
                        </p>
                    </div>

                    {/* Row: type + difficulty + time */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">Type</Label>
                            <select
                                value={editType}
                                onChange={e => setEditType(e.target.value)}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                {QUESTION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Difficulty</Label>
                            <select
                                value={editDiff}
                                onChange={e => setEditDiff(e.target.value)}
                                className="flex h-9 w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                                {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">Time limit (seconds)</Label>
                            <Input
                                type="number"
                                min={30}
                                max={3600}
                                step={30}
                                value={editTimeSecs}
                                onChange={e => setEditTimeSecs(e.target.value)}
                                className="h-9 text-sm"
                            />
                            <p className="text-[10px] text-muted-foreground">{secsToLabel(editTimeSecs)}</p>
                        </div>
                    </div>

                    {/* Question text */}
                    <div className="space-y-1.5">
                        <Label className="text-xs flex items-center gap-1.5">
                            <MessageSquare className="h-3 w-3" /> Question text
                        </Label>
                        <textarea
                            value={editText}
                            onChange={e => setEditText(e.target.value)}
                            rows={3}
                            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                    </div>

                    {/* MCQ options */}
                    {editFormat === 'mcq' && (
                        <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-1.5">
                                <ListChecks className="h-3 w-3" /> Answer options
                                <span className="text-muted-foreground font-normal">(select the correct one)</span>
                            </Label>
                            <div className="space-y-2">
                                {editOptions.map((opt, i) => (
                                    <div key={opt.label} className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCorrect(i)}
                                            title="Mark as correct answer"
                                            className={cn(
                                                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                                                opt.isCorrect
                                                    ? 'border-emerald-500 bg-emerald-500'
                                                    : 'border-border bg-background hover:border-emerald-500/50',
                                            )}
                                        >
                                            {opt.isCorrect && <Check className="h-2.5 w-2.5 text-white" />}
                                        </button>
                                        <span className="w-5 shrink-0 text-[11px] font-bold text-muted-foreground">
                                            {opt.label}.
                                        </span>
                                        <Input
                                            value={opt.text}
                                            onChange={e => setOptionText(i, e.target.value)}
                                            placeholder={`Option ${opt.label}`}
                                            className={cn(
                                                'h-8 text-sm flex-1',
                                                opt.isCorrect && 'border-emerald-500/50 bg-emerald-500/5',
                                            )}
                                        />
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                                The circled option is marked correct. Auto-score: correct = 100, wrong = 0.
                            </p>
                        </div>
                    )}

                    {/* Ideal answer (verbal only) */}
                    {editFormat === 'verbal' && (
                        <div className="space-y-1.5">
                            <Label className="text-xs flex items-center gap-1.5">
                                <Lightbulb className="h-3 w-3" /> Ideal answer (used for AI scoring)
                            </Label>
                            <textarea
                                value={editIdeal}
                                onChange={e => setEditIdeal(e.target.value)}
                                rows={3}
                                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            />
                        </div>
                    )}

                    {/* Keywords (shown for both formats) */}
                    <div className="space-y-1.5">
                        <Label className="text-xs">Scoring keywords</Label>
                        <TagInput
                            value={editKeywords}
                            onChange={setEditKeywords}
                            placeholder="Add keyword, press Enter…"
                            maxTags={10}
                        />
                        <p className="text-[10px] text-muted-foreground">
                            {editFormat === 'mcq'
                                ? 'AI checks for these in the verbal recording alongside the MCQ selection.'
                                : '3–6 terms the AI checks for in candidate answers.'}
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
                            <X className="mr-1.5 h-3.5 w-3.5" /> Cancel
                        </Button>
                        <Button type="button" size="sm" onClick={applyEdit}
                            disabled={!editText.trim()}
                        >
                            <Check className="mr-1.5 h-3.5 w-3.5" /> Apply
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default QuestionCard;
